import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Emoji } from '@cobu/types';
import { defaultEmojis, mergeEmojiSets } from '../emoji';
import { fetchCustomEmojis } from '@cobu/api-client';

interface EmojiContextValue {
  emojis: Emoji[];
  byTitle: Record<string, Emoji>;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

const EmojiContext = createContext<EmojiContextValue | undefined>(undefined);

export const EmojiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const buildingUuid = useSelector((s: RootState) => s.auth.user?.building?.uuid);

  const query = useQuery({
    queryKey: ['building', 'emojis', buildingUuid],
    queryFn: () => fetchCustomEmojis(buildingUuid!),
    enabled: !!buildingUuid,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const emojis = useMemo<Emoji[]>(() => {
    if (!buildingUuid) return defaultEmojis;
    const custom = query.data || [];
    return mergeEmojiSets(defaultEmojis, custom);
  }, [buildingUuid, query.data]);

  const value = useMemo<EmojiContextValue>(() => {
    const byTitle: Record<string, Emoji> = {};
    for (const e of emojis) {
      byTitle[e.title] = e;
    }
    return {
      emojis,
      byTitle,
      isLoading: buildingUuid ? query.isLoading : false,
      error: buildingUuid ? query.error?.message ?? null : null,
      reload: () => {
        if (buildingUuid) void query.refetch();
      },
    };
  }, [emojis, query.isLoading, query.error, query.refetch, buildingUuid]);

  return <EmojiContext.Provider value={value}>{children}</EmojiContext.Provider>;
};

export function useEmojiPalette(): EmojiContextValue {
  const ctx = useContext(EmojiContext);
  if (!ctx) throw new Error('useEmojiPalette must be used within EmojiProvider');
  return ctx;
} 